import { useState, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import CustomTextInput from '@/components/CustomTextInput'
import CountrySelector from '@/components/CountrySelector'
import CountryCodeSelector from '@/components/CountryCodeSelector'
import { DatetimePicker, Popup, Picker } from 'react-vant'
import { useOccupationListStatus, OccupationType } from '@/hooks/listStatus'
import { type KycFormData, isAtLeast18YearsOld } from '../types'
import dayjs from 'dayjs'

interface KycPersonalInfoFormProps {
  formData: KycFormData
  updateFormData: (data: Partial<KycFormData>) => void
  readonly?: boolean
}

// 性别选项（需要在组件内使用 t 函数）
const getGenderOptions = (t: (key: string) => string) => [
  { value: 'male', label: t('男') },
  { value: 'female', label: t('女') },
]

// 职业枚举列表
const occupationValues = Object.values(OccupationType)

// 邮箱验证正则
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
// 手机号验证正则（6-15位数字）
const PHONE_REGEX = /^\d{6,15}$/

// 判断是否为中文地区（中国大陆、台湾、香港、澳门）
const isChineseRegion = (countryCode: string) => {
  return ['CN', 'TW', 'HK', 'MO'].includes(countryCode?.toUpperCase())
}

export default function KycPersonalInfoForm({ formData, updateFormData, readonly = false }: KycPersonalInfoFormProps) {
  const { t } = useTranslation()
  const { getOccupationListStatus } = useOccupationListStatus()
  const genderOptions = getGenderOptions(t)
  
  // 根据国家/地区动态设置姓名 placeholder
  // 中国/港/台/澳：强制中文"张"/"三"；其他地区：统一英文"Zhang"/"San"（国家选择优先级 > 系统语言）
  const isChinese = useMemo(() => isChineseRegion(formData.address_country), [formData.address_country])
  const lastNamePlaceholder = useMemo(() => isChinese ? '张' : 'Zhang', [isChinese])
  const firstNamePlaceholder = useMemo(() => isChinese ? '三' : 'San', [isChinese])

  // Popup 控制状态
  const [showGenderPicker, setShowGenderPicker] = useState(false)
  const [showBirthdayPicker, setShowBirthdayPicker] = useState(false)
  const [showOccupationPicker, setShowOccupationPicker] = useState(false)

  // 邮箱与手机号校验状态（仅在有输入内容时校验格式）
  const emailError = useMemo(() => {
    if (!formData.email) return ''  
    return EMAIL_REGEX.test(formData.email) ? '' : t('请输入有效的邮箱地址X')
  }, [formData.email, t])

  const phoneError = useMemo(() => {
    if (!formData.phone) return ''
    return PHONE_REGEX.test(formData.phone) ? '' : t('请输入有效的手机号X位数字')
  }, [formData.phone, t])

  // 出生日期未满18岁提示
  const birthDateError = useMemo(() => {
    if (!formData.birth_date?.trim()) return ''
    return isAtLeast18YearsOld(formData.birth_date) ? '' : t('需年满18周岁')
  }, [formData.birth_date, t])

  // 邮箱输入处理
  const handleEmailChange = useCallback((value: string) => {
    if (readonly) return
    updateFormData({ email: value })
  }, [updateFormData, readonly])

  // 手机号输入处理
  const handlePhoneChange = useCallback((value: string) => {
    if (readonly) return
    // 只允许输入数字
    const numericValue = value.replace(/[^\d]/g, '')
    updateFormData({ phone: numericValue })
  }, [updateFormData, readonly])

  // 获取性别显示文本
  const getGenderLabel = (value: string) => {
    const option = genderOptions.find(opt => opt.value === value)
    return option ? option.label : ''
  }

  // 获取职业选项
  const occupationOptions = occupationValues.map(value => ({
    text: getOccupationListStatus(value),
    value: value
  }))

  // 通用选择器输入框组件
  const renderSelectInput = (
    label: string,
    value: string,
    placeholder: string,
    onClick: () => void,
    className?: string,
    isErrored?: boolean
  ) => (
    <div className={className || 'w-full'}>
      <div className="block text-sm text-[#667085] mb-2 font-medium">
        {label}
      </div>
      <div
        onClick={readonly ? undefined : onClick}
        className={`w-full h-12 px-4 rounded-2.5 border border-solid ${isErrored ? 'border-red-500' : 'border-[#EAECF0]'} bg-white flex items-center justify-between ${readonly ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
      >
        <span className={value ? 'text-[#1D2129]' : 'text-[#C9CDD4]/70'}>
          {value || placeholder}
        </span>
        {!readonly && <img src="/icon/select-down.svg" alt="" className="w-3" />}
      </div>
    </div>
  )

  // 通用文本输入框组件
  const renderTextInput = (
    label: string,
    value: string,
    placeholder: string,
    onChange: (val: string) => void,
    className?: string,
    inputMode?: 'numeric' | 'text' | 'decimal' | 'email' | 'tel' | 'url' | 'search',
    maxLength?: number
  ) => (
    <div className={className || 'w-full'}>
      <div className="block text-sm text-[#667085] mb-2 font-medium">
        {label}
      </div>
      <CustomTextInput
        value={value}
        onChangeText={readonly ? () => {} : onChange}
        placeholder={placeholder}
        inputMode={inputMode}
        maxLength={maxLength}
        disabled={readonly}
      />
    </div>
  )

  return (
    <div className="space-y-4">
      {/* 国家/地区 */}
      <div>
        <div className="block text-sm text-[#667085] mb-2 font-medium">
          {t('国家或地区')}
        </div>
        <CountrySelector
          value={formData.address_country}
          onChange={readonly ? () => {} : (value) => updateFormData({ address_country: value })}
          placeholder={t('选择国家或地区X')}
          returnType="value"
          disabled={readonly}
        />
      </div>

      {/* 姓氏 & 名字 */}
      <div className="flex gap-4">
        {renderTextInput(
          t('姓氏'),
          formData.last_name,
          lastNamePlaceholder,
          (val) => updateFormData({ last_name: val }),
          'flex-1'
        )}
        {renderTextInput(
          t('名字'),
          formData.first_name,
          firstNamePlaceholder,
          (val) => updateFormData({ first_name: val }),
          'flex-1'
        )}
      </div>

      {/* 性别 */}
      {renderSelectInput(
        t('性别'),
        getGenderLabel(formData.sex),
        t('选择性别'),
        () => setShowGenderPicker(true)
      )}

      {/* 手机号 */}
      <div>
        <div className="block text-sm text-[#667085] mb-2 font-medium">
          {t('手机号')}
        </div>
        <div className={`flex items-center h-12 rounded-2.5 border border-solid bg-white ${phoneError ? 'border-red-500' : 'border-[#EAECF0]'} ${readonly ? 'opacity-60' : ''}`}>
          <div className={`h-full flex items-center pl-4 pr-2 [&>div]:border-none [&>div]:h-auto [&>div]:p-0 [&>div]:rounded-none ${readonly ? 'pointer-events-none' : 'cursor-pointer'}`}>
            <CountryCodeSelector
              value={formData.phone_zone_number}
              onChange={readonly ? () => {} : (dialCode) => updateFormData({ phone_zone_number: dialCode })}
            />
          </div>
          <input
            type="tel"
            value={formData.phone}
            onChange={(e) => handlePhoneChange(e.target.value)}
            placeholder={t('输入手机号')}
            inputMode="numeric"
            disabled={readonly}
            className={`flex-1 h-full px-3 outline-none text-[#1D2129] placeholder-[#C9CDD4] bg-transparent ${readonly ? 'cursor-not-allowed' : ''}`}
          />
        </div>
        {phoneError && (
          <div className="text-xs text-red-500 mt-1">{phoneError}</div>
        )}
      </div>

      {/* 电子邮箱 */}
      <div>
        <div className="block text-sm text-[#667085] mb-2 font-medium">
          {t('电子邮箱')}
        </div>
        <CustomTextInput
          value={formData.email}
          onChangeText={handleEmailChange}
          placeholder={t('输入电子邮箱')}
          inputMode="email"
          isErrored={!!emailError}
          disabled={readonly}
        />
        {emailError && (
          <div className="text-xs text-red-500 mt-1">{emailError}</div>
        )}
      </div>

      {/* 证件颁发国家或地区 */}
      <div>
        <div className="block text-sm text-[#667085] mb-2 font-medium">
          {t('证件颁发国家或地区')}
        </div>
        <CountrySelector
          value={formData.nationality}
          onChange={readonly ? () => {} : (value) => updateFormData({ nationality: value })}
          placeholder={t('请选择')}
          returnType="value"
          disabled={readonly}
        />
      </div>

      {/* 出生日期 */}
      <div className="w-full">
        {renderSelectInput(
          t('出生日期'),
          formData.birth_date ? dayjs(formData.birth_date).format('YYYY-MM-DD') : '',
          'YYYY-MM-DD',
          () => setShowBirthdayPicker(true),
          undefined,
          !!birthDateError
        )}
        {birthDateError && (
          <div className="text-xs text-red-500 mt-1">{birthDateError}</div>
        )}
      </div>

      {/* 职务 */}
      {renderSelectInput(
        t('职务'),
        formData.occupation ? getOccupationListStatus(formData.occupation) : '',
        t('选择职务'),
        () => setShowOccupationPicker(true)
      )}

      {/* 省份 & 城市 */}
      <div className="flex gap-4">
        {renderTextInput(
          t('省份'),
          formData.address_province,
          t('所在省份'),
          (val) => updateFormData({ address_province: val }),
          'flex-1'
        )}
        {renderTextInput(
          t('城市'),
          formData.address_city,
          t('所在城市'),
          (val) => updateFormData({ address_city: val }),
          'flex-1'
        )}
      </div>

      {/* 详细地址 */}
      <div>
        <div className="block text-sm text-[#667085] mb-2 font-medium">
          {t('详细地址')}
        </div>
        <textarea
          value={formData.address}
          onChange={readonly ? undefined : (e) => updateFormData({ address: e.target.value })}
          placeholder={t('填写详细地址')}
          rows={3}
          disabled={readonly}
          className={`w-full px-4 py-3 rounded-2.5 border border-solid border-[#EAECF0] bg-white text-[#1D2129] placeholder-[#C9CDD4] resize-none focus:border-[#667085] ${readonly ? 'cursor-not-allowed opacity-60' : ''}`}
        />
      </div>

      {/* 性别选择器 */}
      {!readonly && (
        <Popup
          visible={showGenderPicker}
          position="bottom"
          round
          onClose={() => setShowGenderPicker(false)}
        >
          <Picker
            title={t('选择性别')}
            placeholder={t('请选择')}
            cancelButtonText={t('取消')}
            confirmButtonText={t('确定')}
            columns={genderOptions.map(opt => ({ text: opt.label, value: opt.value }))}
            onConfirm={(value: string) => {
              updateFormData({ sex: value })
              setShowGenderPicker(false)
            }}
            onCancel={() => setShowGenderPicker(false)}
          />
        </Popup>
      )}

      {/* 出生日期选择器 */}
      {!readonly && (
        <Popup
          visible={showBirthdayPicker}
          position="bottom"
          round
          onClose={() => setShowBirthdayPicker(false)}
        >
          <DatetimePicker
            type="date"
            title={t('选择出生日期')}
            cancelButtonText={t('取消')}
            confirmButtonText={t('确定')}
            minDate={new Date(1900, 0, 1)}
            maxDate={new Date()}
            value={formData.birth_date ? dayjs(formData.birth_date).toDate() : new Date(2000, 0, 1)}
            onConfirm={(value: Date) => {
              updateFormData({ birth_date: dayjs(value).format('YYYY-MM-DD') })
              setShowBirthdayPicker(false)
            }}
            onCancel={() => setShowBirthdayPicker(false)}
          />
        </Popup>
      )}

      {/* 职务选择器 */}
      {!readonly && (
        <Popup
          visible={showOccupationPicker}
          position="bottom"
          round
          onClose={() => setShowOccupationPicker(false)}
        >
          <Picker
            title={t('选择职务')}
            cancelButtonText={t('取消')}
            confirmButtonText={t('确定')}
            placeholder={t('请选择')}
            columns={occupationOptions}
            onConfirm={(value: string) => {
              updateFormData({ occupation: value })
              setShowOccupationPicker(false)
            }}
            onCancel={() => setShowOccupationPicker(false)}
          />
        </Popup>
      )}

    </div>
  )
}
